import React, {useCallback, useEffect, useState} from 'react';
import {withRouter} from 'react-router-dom';
import LayoutContainer from '../../components/LayoutContainer';
import Box from '../../components/Box';
import MainContainer from '../../components/MainContainer';
import Logo from '../../components/Logo';
import DisplayIban from '../../components/DisplayIban';
import SocialShareButton from '../../components/SocialShareButton';
import {getFormData} from '@nexios/frontend-forms';
import {gaStep4} from '../../googleAnalytics/step4';
import {sendRevenue} from '../../vwo/sendRevenue';
import {connect} from 'react-redux';
import parseText from '../../utils/parseText';
import {subscribeToOptIn, addPersonalMessage} from '../../reducer';
import {Helmet} from 'react-helmet';
import useScrollToTop from '../../hooks/useScrollToTop';
import AnonOptIn from '../../components/AnonOptIn';
import PersonalMessageForm from '../../components/PersonalMessageForm';
import Button from '../../components/Button';
import {withTheme} from 'styled-components';
import DJSBSideBar from '../../components/DJSBSideBar';
import CollectingImage from '../../components/CollectingImage';
import Title from '../../components/Title';
import ProgressBar from '../../components/ProgressBar';

const Step4 = ({formContext, dmBoxInfo, collectorInfo, formData, gaStep4, sendRevenueToVwo, isOptInDone, saveOptIn, isMessageAdded, saveMessage, themeName, isProductForm, theme, progressBar}) => {
  useScrollToTop();

  const [facebookUrl, setFacebookUrl] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [whatsappUrl, setWhatsappUrl] = useState('');
  const [twitterUrl, setTwitterUrl] = useState('');
  const [thankYouTitle, setThankYouTitle] = useState('');
  const [thankYouText, setThankYouText] = useState('');

  useEffect(() => {
    const {externalVariable} = formData;
    const {redirectToExternal, redirectToExternalUrl, redirectToExternalTimeout} = formContext;

    if (!redirectToExternal) {
      return;
    }

    if (!externalVariable) {
      return;
    }

    if (!redirectToExternalUrl) {
      return;
    }

    const t = setTimeout(() => {
      window.location = redirectToExternalUrl.replace(/(\{0})/g, externalVariable);
    }, redirectToExternalTimeout || 5000);

    return () => {
      clearTimeout(t);
    };

  }, []);

  useEffect(() => {
    gaStep4(formContext);

    if (window.isVwoEnabled && !isProductForm) {
      sendRevenueToVwo(formContext);
    }
  }, [gaStep4, formContext, isProductForm]);

  useEffect(() => {
    const url = encodeURIComponent(document.querySelector('meta[property=\'og:url\']').getAttribute('content'));

    const socialMediaTitle = encodeURIComponent(formContext.socialMediaTitle);
    setFacebookUrl(`https://www.facebook.com/sharer.php?u=${url}&t=${socialMediaTitle}`);
    setLinkedinUrl(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`);
    setWhatsappUrl(`https://wa.me/?text=${socialMediaTitle}%20${url}`);

    if (formContext.twitterTitle) {
      const twitterTitle = encodeURIComponent(formContext.twitterTitle);
      setTwitterUrl(`https://twitter.com/share?text=${twitterTitle}&url=${url}`);
    } else {
      setTwitterUrl(`https://twitter.com/share?text=${socialMediaTitle}&url=${url}`);
    }

    const isOneOff = formData.frequency === 'oneOff';
    const isDirectDebit = formData.paymentMethod === 'directDebit';

    let titleTemplate;
    let textTemplate;

    if (isOneOff) {
      if (isDirectDebit) {
        titleTemplate = formContext.oneOffThankYouTitle;
        textTemplate = formContext.oneOffThankYouTextHtml_txt;
      } else {
        titleTemplate = formContext.donationThankYouTitle;
        textTemplate = formContext.donationThankYouTextHtml_txt;
      }
    } else if (formContext.mandateType === 'temporary') {
      titleTemplate = formContext.temporaryThankYouTitle;
      textTemplate = formContext.temporaryThankYouTextHtml_txt;
    } else {
      titleTemplate = formContext.continuousThankYouTitle;
      textTemplate = formContext.continuousThankYouTextHtml_txt;
    }

    setThankYouTitle(parseText(formData, titleTemplate));
    setThankYouText(parseText({...formData, ...formContext}, textTemplate, themeName));
  }, [formContext, formData, themeName]);

  const goToExternal = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();

    const {externalVariable} = formData;
    const {redirectToExternalUrl} = formContext;

    window.location = redirectToExternalUrl.replace(/(\{0})/g, externalVariable);
  });

  return <>
    <Helmet>
      <title>{(formContext.isDonationBox && dmBoxInfo?.boxHeader_txt) || formContext.thankYouBrowserTitle}</title>
    </Helmet>
    <MainContainer
      backgroundImageUrl={theme?.donationBox?.imageAsBackground && formContext.isDonationBox && dmBoxInfo?.image?.publicUrl
        ? dmBoxInfo.image.publicUrl
        : formContext.backgroundImageUrl}
      backgroundImageTabletUrl={theme?.donationBox?.imageAsBackground && formContext.isDonationBox && dmBoxInfo?.image?.publicUrl
        ? dmBoxInfo.image.publicUrl
        : formContext.backgroundImageTabletUrl}
      backgroundImageMobileUrl={theme?.donationBox?.imageAsBackground && formContext.isDonationBox && dmBoxInfo?.image?.publicUrl
        ? dmBoxInfo.image.publicUrl
        : formContext.backgroundImageMobileUrl}
    />
    <LayoutContainer
      position={formContext.textPosition}
      header={<div>
        <div style={{float: 'left', display: 'inline-block'}}>
          <Logo url={formContext.websiteUrl} />
        </div>
        {formContext.isCollecting && theme?.collecting?.imageTopRight &&
          <div style={{float: 'right', display: 'inline-block'}}><CollectingImage collectorInfo={collectorInfo} /></div>}
        <DisplayIban iban='NL08 INGB 0000 0005 55' />
      </div>}
      content={<><Box>
        {theme?.title?.showOnThankYou && <Title title={formContext.step4Header}><ProgressBar progress={progressBar} /></Title>}
        <div className='widgetContent'>
          <h1>{thankYouTitle}</h1>
          <div className='field'>
            <div dangerouslySetInnerHTML={{__html: thankYouText}} />
          </div>

          {formContext.redirectToExternal && formContext.redirectToExternalButtonText && <div><Button onClick={goToExternal} label={formContext.redirectToExternalButtonText} /></div>}

          <AnonOptIn saveOptIn={saveOptIn} isOptInDone={isOptInDone} formContext={formContext} />
          <PersonalMessageForm saveMessage={saveMessage} isMessageAdded={isMessageAdded} formContext={formContext} />

          {formContext.allowSocialShare && <div style={{display: 'flex', justifyContent: 'space-evenly'}}>
            <SocialShareButton name='whatsapp' url={whatsappUrl} />
            <SocialShareButton name='facebook' url={facebookUrl} />
            <SocialShareButton name='linkedin' url={linkedinUrl} />
            <SocialShareButton name='twitter' url={twitterUrl} />
          </div>}
        </div>
      </Box>
        {formContext.allowCallToAction &&
          <div dangerouslySetInnerHTML={{__html: formContext.callToAction}} />}
      </>}
      sidebar={<>
        {theme?.djsb?.showDJSB && <DJSBSideBar formContext={formContext}/>}
      </>}
      footer={formContext.footer}
      formContext={formContext}
    />
  </>;
};

const mapStateToProps = (state) => {
  return {
    formContext: state.reducer.formContext,
    formData: getFormData(state, 'donate'),
    isOptInDone: state.reducer.isOptInDone,
    isMessageAdded: state.reducer.isMessageAdded,
    themeName: state.reducer.theme,
    isProductForm: state.reducer.isProductForm
  };
};

const mapDispatchToProps = (dispatch) => ({
  gaStep4: (formContext) => {
    dispatch(gaStep4(formContext));
  },
  sendRevenueToVwo: (formContext) => {
    dispatch(sendRevenue(formContext));
  },
  saveOptIn: (campaignPropositionCode, pageVariantKey, email, telephone) => {
    dispatch(
      subscribeToOptIn(
        campaignPropositionCode,
        pageVariantKey,
        email,
        telephone
      )
    );
  },
  saveMessage: (campaignPropositionCode, pageVariantKey, message, name) => {
    dispatch(addPersonalMessage(campaignPropositionCode, pageVariantKey, message, name));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withTheme(Step4)));

